import { type ChainFullId, ChainFullIds } from "@umami/chains";

import { Bridge, BridgeDirection } from "../bridge";
import { type LayerZeroBridgeQuoteResponse } from "../types";

export class LayerZeroBridgeQuoteError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LayerZeroBridgeQuoteError";
  }
}

export function calculateDirection(
  sellToken: { chainId: ChainFullId },
  buyToken: { chainId: ChainFullId }
): BridgeDirection {
  if (sellToken.chainId === ChainFullIds.ETHEREUM && buyToken.chainId === ChainFullIds.ETHERLINK) {
    return BridgeDirection.EthereumToEtherlink;
  } else if (
    sellToken.chainId === ChainFullIds.ETHERLINK &&
    buyToken.chainId === ChainFullIds.ETHEREUM
  ) {
    return BridgeDirection.EtherlinkToEthereum;
  } else if (sellToken.chainId === ChainFullIds.ETHEREUM && buyToken.chainId === ChainFullIds.BSC) {
    return BridgeDirection.EthereumToBsc;
  } else if (sellToken.chainId === ChainFullIds.BSC && buyToken.chainId === ChainFullIds.ETHEREUM) {
    return BridgeDirection.BscToEthereum;
  } else if (
    sellToken.chainId === ChainFullIds.ETHERLINK &&
    buyToken.chainId === ChainFullIds.BSC
  ) {
    return BridgeDirection.EtherlinkToBsc;
  } else if (
    sellToken.chainId === ChainFullIds.BSC &&
    buyToken.chainId === ChainFullIds.ETHERLINK
  ) {
    return BridgeDirection.BscToEtherlink;
  } else {
    throw new LayerZeroBridgeQuoteError(
      `Unsupported bridge direction: ${sellToken.chainId} to ${buyToken.chainId}`
    );
  }
}

export interface LayerZeroBridgeQuoteParams {
  sellToken: {
    chainId: ChainFullId;
    symbol: string;
    decimals: number;
    address?: string;
  };
  buyToken: {
    chainId: ChainFullId;
    symbol: string;
    decimals: number;
    address?: string;
  };
  sellAmount: string;
  network: "mainnet" | "shadownet";
}

export async function getLayerZeroBridgeQuote({
  sellToken,
  buyToken,
  sellAmount,
  network,
}: LayerZeroBridgeQuoteParams): Promise<LayerZeroBridgeQuoteResponse> {
  try {
    if (!sellAmount) {
      throw new LayerZeroBridgeQuoteError("Sell amount is required");
    }

    const direction = calculateDirection(sellToken, buyToken);

    const bridge = new Bridge({ network });

    const { nativeFee, zroFee } = await bridge.estimateFee({
      direction,
      useZro: false,
    });

    let nativeFeeSymbol = "ETH";

    if (sellToken.chainId === ChainFullIds.ETHERLINK) {
      nativeFeeSymbol = "XTZ";
    } else if (sellToken.chainId === ChainFullIds.ETHEREUM) {
      nativeFeeSymbol = "ETH";
    }

    // For LayerZero bridge, the buy amount is typically the same as sell amount (1:1 ratio)
    const buyAmount = sellAmount;

    return {
      direction: direction,
      sellAmount,
      buyAmount,
      nativeFee,
      zroFee,
      nativeFeeSymbol,
      provider: "LayerZero",
    };
  } catch (error) {
    console.error("Error getting LayerZero bridge quote:", error);

    if (error instanceof LayerZeroBridgeQuoteError) {
      throw error;
    }

    throw new LayerZeroBridgeQuoteError(
      `Failed to get LayerZero bridge quote: ${error instanceof Error ? error.message : "Unknown error"}`
    );
  }
}
